import * as React from 'react';
import { createRendererWithUniDriver } from '../../test/utils';
import { Range, RangeProps } from '.';
import { rangePrivateDriverFactory } from './Range.private.driver';

const defaultProps: RangeProps = {
  max: 100,
};

describe('Range', () => {
  const createPrivateDriver = createRendererWithUniDriver(
    rangePrivateDriverFactory,
  );

  const bootstrap = (props: RangeProps) => {
    const Component = <Range {...props} />;

    return createPrivateDriver(Component);
  };

  it('should render', async () => {
    const { driver } = bootstrap(defaultProps);
    expect(await driver.exists()).toBe(true);
  });

  it('should set min value to 0 by default', async () => {
    const { driver } = bootstrap(defaultProps);
    expect(await driver.getMinValue()).toBe(0);
  });

  it('should set min value', async () => {
    const { driver } = bootstrap({ ...defaultProps, min: 20 });
    expect(await driver.getMinValue()).toBe(20);
  });

  it('should set max value', async () => {
    const { driver } = bootstrap({ ...defaultProps, max: 60 });
    expect(await driver.getMaxValue()).toBe(60);
  });

  it('should set default value for undefined', async () => {
    const { driver } = bootstrap({ ...defaultProps, value: undefined });
    expect(await driver.getSelectedValue()).toEqual([0, 100]);
  });

  it('should set selected value', async () => {
    const { driver } = bootstrap({ ...defaultProps, value: [35, 75] });
    expect(await driver.getSelectedValue()).toEqual([35, 75]);
  });

  it('should set min aria label', async () => {
    const { driver } = bootstrap({
      ...defaultProps,
      value: [35, 75],
      minAriaLabel: 'minAriaLabel',
    });
    expect(await driver.getMinAriaLabel()).toEqual('minAriaLabel');
  });

  it('should set max aria label', async () => {
    const { driver } = bootstrap({
      ...defaultProps,
      value: [35, 75],
      maxAriaLabel: 'maxAriaLabel',
    });
    expect(await driver.getMaxAriaLabel()).toEqual('maxAriaLabel');
  });

  it.each([
    ['USD', undefined, ['$3,500.00', '$7,500.00']],
    ['USD', 'en', ['$3,500.00', '$7,500.00']],
    ['USD', 'lt', ['3 500,00 USD', '7 500,00 USD']],
    ['EUR', 'en', ['€3,500.00', '€7,500.00']],
    ['JPY', 'en', ['¥3,500', '¥7,500']],
    ['CNY', 'en', ['CN¥3,500.00', 'CN¥7,500.00']],
  ])(
    'should display currency in correct format for %p with %p locale',
    async (currency, locale, expectedValue) => {
      const { driver } = bootstrap({
        ...defaultProps,
        value: [3500, 7500],
        format: { locale, style: 'currency', currency },
      });
      expect(await driver.getSelectedValueText()).toEqual(expectedValue);
    },
  );

  it('should display label', async () => {
    const { driver } = bootstrap({
      ...defaultProps,
      label: 'This is some label',
    });
    expect(await driver.getLabelText()).toBe('This is some label');
  });

  it('should display slider component in disabled state', async () => {
    const { driver } = bootstrap({ ...defaultProps, disabled: true });
    expect(await driver.isSliderDisabled()).toBeTruthy();
  });

  it('should get correct error message in error state', async () => {
    const { driver } = bootstrap({
      ...defaultProps,
      errorMessage: 'error message',
    });
    expect(await driver.getErrorMessage()).toBe('error message');
  });
});
